import { useEffect, useMemo, useRef, useState } from "react";
import type { Style as GeoStylerStyle } from "geostyler-style";
import { getCmTableGeo, getCmTableBulkGeo } from "../services/cmTables";
import { ApiEndpoint, MapLibreLayerSpecification } from "../types";
import { getBboxByGeom, isBboxContained } from "../utilities/bboxUtils";
import { geostylerToMapLibreLayers } from "../utilities/geostylerToMapLibre";
import { tableStyleToGeostyler } from "../utilities/legacyStyleToGeostyler";
import { baseGeometry } from "../utilities/cmTableStyle";
import { useMapActions, useMapStore } from "../store/map";
import { useLayerActions, useLayerSelector } from "../store/layer";

/**
 * useCmTableMapLayers
 *
 * Two-track strategy:
 *
 * Track A – Selected table:
 *   Fetches all features once (no bbox) to allow extent calculation and flying.
 *
 * Track B – Background tables:
 *   Fetches features within viewport via bulk-geojson API.
 *   Optimization: Only refetches if the map moves OUTSIDE the previously fetched area.
 *   Zooming in (smaller bbox) does NOT trigger a refetch if the area is already covered.
 */

/**
 * A CM-table style is a stored GeoStyler {@link GeoStylerStyle} (the migrated
 * source of truth) when it carries a `rules` array. Legacy `match`-shorthand
 * styles (`iconStyle`/`line-color`/…) are converted to GeoStyler on the fly so
 * both render through the single shared converter.
 */
const isGeostylerStyle = (style?: Record<string, any>): style is GeoStylerStyle => !!style && Array.isArray((style as any).rules);

/** Ultimate fallback colour when no `defaultColor` (theme primary) is supplied. */
const DEFAULT_FEATURE_COLOR = "#3b82f6";

/**
 * Sub-layer id suffixes used for mixed-geometry ("geometry") CM tables.
 *
 * A MapLibre layer renders exactly one geometry family (circle→points,
 * line→lines, fill→polygons), so a table whose rows can each be a point, line
 * or polygon is drawn with three sibling layers over ONE shared GeoJSON source,
 * each scoped by a `$type` filter. The point layer keeps the canonical
 * `cm-table-<slug>` id; the line/fill siblings append these suffixes.
 */
const CM_SUBLAYER_SUFFIX = { line: "::line", fill: "::fill", point: "::point" } as const;

/**
 * Strip a sub-layer suffix, returning the canonical `cm-table-<slug>` base id.
 * Covers both mixed-geometry siblings (`::line`/`::fill`/`::point`) and GeoStyler
 * multi-fragment siblings (`::p1`, `::p2`, …). Table slugs never contain `::`.
 */
export const stripCmSubLayerSuffix = (layerId: string): string => layerId.replace(/::.+$/, "");

/** True when `layerId` (base or sub-layer) belongs to the table keyed by `baseId`. */
const belongsToBaseLayer = (layerId: string, baseId: string): boolean => stripCmSubLayerSuffix(layerId) === baseId;

// `$type` normalises Multi* geometries to their base family, so these filters
// also catch MultiPoint / MultiLineString / MultiPolygon features.
const POINT_FILTER = ["==", "$type", "Point"];
const LINE_FILTER = ["==", "$type", "LineString"];
const POLYGON_FILTER = ["==", "$type", "Polygon"];

/**
 * Attaches a GeoJSON source + stable ids to the renderer fragments produced by
 * our shared GeoStyler → MapLibre converter. Fragment 0 keeps the canonical
 * `cm-table-<slug>` id (the row shown in the Layers panel); extra fragments get a
 * `::p{i}` sub-layer id. Icon `image` paths are resolved to CDN URLs inside the
 * converter and loaded lazily by MapComponent's `styleimagemissing` handler.
 */
function geostylerSpecs(
  baseId: string,
  gs: GeoStylerStyle,
  geojson: any,
  spatialType: string,
  displayName?: string
): MapLibreLayerSpecification[] {
  const source = { id: baseId, type: "geojson" as const, data: geojson };
  return geostylerToMapLibreLayers(gs).map((frag, i) => {
    // Stored styles omit fill-opacity / line-width (they rely on renderer defaults),
    // but the selection/highlight pipeline needs a concrete base value and translucent
    // fills match the pre-migration CM-table look.
    const paint: Record<string, unknown> = { ...(frag.paint ?? {}) };
    if (frag.type === "fill" && paint["fill-opacity"] === undefined) paint["fill-opacity"] = 0.4;
    if (frag.type === "line" && paint["line-width"] === undefined) paint["line-width"] = 3;

    const spec: Record<string, unknown> = {
      id: i === 0 ? baseId : `${baseId}::p${i}`,
      type: frag.type,
      source,
      layout: { visibility: "visible", ...(frag.layout ?? {}) },
      metadata: frag.metadata,
      initialVisibility: true,
      disableClicking: false,
      _spatialType: spatialType,
      displayName
    };
    if (i > 0) spec._cmSubLayer = true;
    if (Object.keys(paint).length) spec.paint = paint;
    if (frag.filter) spec.filter = frag.filter;
    if (frag.minzoom != null) spec.minzoom = frag.minzoom;
    if (frag.maxzoom != null) spec.maxzoom = frag.maxzoom;
    return spec as unknown as MapLibreLayerSpecification;
  });
}

/**
 * A plain default layer (no data-driven styling) for tables without a usable
 * style: circle for points, line for lines, fill for polygons — all painted with
 * the theme fallback colour.
 */
function defaultGeometrySpec(
  layerId: string,
  effectiveType: "point" | "polygon" | "linestring" | "none",
  geojson: any,
  defaultColor: string,
  spatialType: string,
  displayName?: string,
  filter?: any[],
  subLayer?: boolean
): MapLibreLayerSpecification {
  const source = { id: layerId, type: "geojson" as const, data: geojson };
  const common: Record<string, unknown> = {
    id: layerId,
    source,
    initialVisibility: true,
    disableClicking: false,
    _spatialType: spatialType,
    displayName,
    ...(subLayer ? { _cmSubLayer: true } : {}),
    ...(filter ? { filter } : {})
  };

  if (effectiveType === "linestring") {
    return {
      ...common,
      type: "line",
      layout: { visibility: "visible", "line-join": "round", "line-cap": "round" },
      paint: { "line-width": 3, "line-color": defaultColor }
    } as any;
  }
  if (effectiveType === "polygon" || effectiveType === "none") {
    return {
      ...common,
      type: "fill",
      layout: { visibility: "visible" },
      paint: { "fill-opacity": 0.4, "fill-color": defaultColor, "fill-outline-color": defaultColor }
    } as any;
  }
  return {
    ...common,
    type: "circle",
    layout: { visibility: "visible" },
    paint: { "circle-radius": 7, "circle-color": defaultColor, "circle-stroke-width": 1.5, "circle-stroke-color": "#ffffff" }
  } as any;
}

/**
 * Build the MapLibre layer spec(s) for a single CM-table endpoint from its stored
 * GeoStyler style (the single source of truth). Legacy `match`-shorthand styles are
 * converted to GeoStyler on the fly so both render through one converter; the
 * GeoStyler rules filter on `properties.style_value` (exposed by the serializer),
 * so per-row styling is chosen by the map engine — no backend-resolved icon/colour.
 *
 * Tables without a usable style fall back to a plain default layer per geometry.
 * Generic "geometry" tables without a style yield three `$type`-filtered siblings
 * sharing one source so each row renders in whichever family its geometry is.
 */
function buildLayerSpecs(
  baseId: string,
  style: Record<string, any> | undefined,
  spatialType: string,
  geojson: any,
  defaultColor: string,
  displayName?: string
): MapLibreLayerSpecification[] {
  const gs = isGeostylerStyle(style) ? style : tableStyleToGeostyler(style, spatialType);

  if (gs.rules.length > 0) {
    return geostylerSpecs(baseId, gs, geojson, spatialType, displayName);
  }

  // No usable style → plain default rendering by geometry.
  if (spatialType === "geometry") {
    const point = defaultGeometrySpec(baseId, "point", geojson, defaultColor, "geometry", displayName, POINT_FILTER);
    const line = defaultGeometrySpec(
      `${baseId}${CM_SUBLAYER_SUFFIX.line}`,
      "linestring",
      geojson,
      defaultColor,
      "geometry",
      displayName,
      LINE_FILTER,
      true
    );
    const fill = defaultGeometrySpec(
      `${baseId}${CM_SUBLAYER_SUFFIX.fill}`,
      "polygon",
      geojson,
      defaultColor,
      "geometry",
      displayName,
      POLYGON_FILTER,
      true
    );
    return [point, line, fill];
  }

  return [defaultGeometrySpec(baseId, baseGeometry(spatialType), geojson, defaultColor, spatialType, displayName)];
}

const EMPTY_FC = { type: "FeatureCollection", features: [] };

interface UseCmTableMapLayersOptions {
  cmTableEndpoints: ApiEndpoint[];
  activeTableSlug: string | null;
  bbox: string | null;
  showOnlyFilter: boolean;
  /** Fallback colour when a row/table has no styling (typically the theme primary). */
  defaultColor?: string;
}

export interface UseCmTableMapLayersResult {
  layers: MapLibreLayerSpecification[];
  selectedTableExtent: number[] | null;
}

export const useCmTableMapLayers = ({
  cmTableEndpoints,
  activeTableSlug,
  bbox,
  showOnlyFilter,
  defaultColor
}: UseCmTableMapLayersOptions): UseCmTableMapLayersResult => {
  const [layers, setLayers] = useState<MapLibreLayerSpecification[]>([]);
  const [selectedTableExtent, setSelectedTableExtent] = useState<number[] | null>(null);
  const { setIsCmTableLoading } = useMapActions();
  const cmTableLayerVersion = useMapStore((state) => state.cmTableLayerVersion);
  const { setLayerVisibility } = useLayerActions();
  const { visibleLayers } = useLayerSelector();

  const activeTableLoadedRef = useRef<string | null>(null);
  // Track the last cmTableLayerVersion we've acted on to detect bumps
  const lastLayerVersionRef = useRef<number>(cmTableLayerVersion);

  // Track which dynamic tables have been explicitly unchecked/hidden by the user
  const explicitlyHiddenRef = useRef<Set<string>>(new Set());

  // Track the most recently fetched bbox for background tables to avoid redundant fetches on zoom-in.
  const lastBulkFetchRef = useRef<{ slugs: string; bbox: number[] } | null>(null);

  const spatialEndpoints = cmTableEndpoints.filter((ep) => ep.spatialType && ep.spatialType !== "none");
  const tableSlugKey = spatialEndpoints.map((ep) => ep.tableSlug || ep.tableName).join(",");

  /**
   * Fingerprint of everything the rendered layer specs are built from *besides*
   * which tables exist — the stored style and the geometry family.
   *
   * The spec-building effect keyed on `tableSlugKey` alone, so re-styling a table
   * in the admin console (same tables, different rules) never rebuilt its layers
   * and the map kept the old colours until a full page reload. Derived from
   * `cmTableEndpoints`, which is memoized on `config.apiEndpoints`, so this only
   * recomputes when the config object itself is replaced.
   */
  const styleSignature = useMemo(
    () =>
      JSON.stringify(
        cmTableEndpoints
          .filter((ep) => ep.spatialType && ep.spatialType !== "none")
          .map((ep) => [ep.tableSlug || ep.tableName, ep.spatialType, ep.style ?? null])
      ),
    [cmTableEndpoints]
  );

  // 1. Track explicit user hides so effect 2 can restore visibility when the active
  //    endpoint changes back to a previously-hidden layer.  Auto-initialization is
  //    intentionally NOT done here — layers start hidden and are only enabled by
  //    effect 2 (activeTableSlug change) or explicit user selection from the drawer.
  useEffect(() => {
    spatialEndpoints.forEach((ep) => {
      const layerId = `cm-table-${ep.tableSlug || ep.tableName}`;
      if (visibleLayers.has(layerId)) {
        explicitlyHiddenRef.current.delete(layerId);
      } else {
        explicitlyHiddenRef.current.add(layerId);
      }
    });
  }, [visibleLayers, tableSlugKey, spatialEndpoints]);

  // 2. Auto-enable active CM Table layer when selected in the sidebar (Your suggestion!)
  useEffect(() => {
    if (activeTableSlug) {
      const activeLayerId = `cm-table-${activeTableSlug}`;
      explicitlyHiddenRef.current.delete(activeLayerId);
      setLayerVisibility(activeLayerId, true);
    }
  }, [activeTableSlug, setLayerVisibility]);

  // 3. Build placeholder specs
  useEffect(() => {
    if (spatialEndpoints.length === 0) {
      activeTableLoadedRef.current = null;
      setLayers([]);
      return;
    }

    const nextLayerIds = new Set(spatialEndpoints.map((ep) => `cm-table-${ep.tableSlug || ep.tableName}`));

    // If the active table's layer ID is no longer present, reset the loaded ref
    if (activeTableLoadedRef.current && !nextLayerIds.has(`cm-table-${activeTableLoadedRef.current}`)) {
      activeTableLoadedRef.current = null;
    }

    setLayers((prev) => {
      // Carry the already-fetched GeoJSON onto the REBUILT specs, keyed by table
      // rather than by fragment id. Every fragment of one table shares a single
      // source object, so a category added by a re-style renders immediately
      // instead of waiting for the next fetch.
      //
      // The previous version reused the whole previous spec whenever the id
      // matched, which preserved the data but also preserved the old paint — so a
      // style change could not reach the map even once this effect did re-run.
      const dataByBaseId = new Map<string, any>();
      prev.forEach((layer: any) => {
        if (typeof layer.source === "string" || !layer.source?.data) return;
        const baseId = stripCmSubLayerSuffix(layer.id);
        if (!dataByBaseId.has(baseId)) dataByBaseId.set(baseId, layer.source.data);
      });

      return spatialEndpoints.flatMap((ep) => {
        const baseId = `cm-table-${ep.tableSlug || ep.tableName}`;
        const data = dataByBaseId.get(baseId) ?? EMPTY_FC;
        return buildLayerSpecs(baseId, ep.style, ep.spatialType!, data, defaultColor ?? DEFAULT_FEATURE_COLOR, ep.name);
      });
    });
  }, [tableSlugKey, styleSignature, defaultColor]);

  // 2. Selected table full fetch (Track A)
  useEffect(() => {
    if (!activeTableSlug) {
      setSelectedTableExtent(null);
      activeTableLoadedRef.current = null;
      return;
    }

    const activeEndpoint = spatialEndpoints.find((ep) => (ep.tableSlug || ep.tableName) === activeTableSlug);
    if (!activeEndpoint) return;

    // A version bump (after row edit) resets the guard so we re-fetch fresh GeoJSON
    if (cmTableLayerVersion !== lastLayerVersionRef.current) {
      lastLayerVersionRef.current = cmTableLayerVersion;
      activeTableLoadedRef.current = null;
    }

    if (activeTableLoadedRef.current === activeTableSlug) return;

    let cancelled = false;

    setIsCmTableLoading(true);
    getCmTableGeo(activeEndpoint.tableSlug || activeEndpoint.tableName)
      .then((res) => {
        if (cancelled) return;

        const layerId = `cm-table-${activeEndpoint.tableSlug || activeEndpoint.tableName}`;
        const geojson = res.data;

        if (geojson.features.length > 0) {
          const extent = getBboxByGeom(geojson);
          if (extent) {
            setSelectedTableExtent(extent as number[]);
          }
        }

        activeTableLoadedRef.current = activeTableSlug;

        setLayers((prev) =>
          prev.map((l: any) =>
            belongsToBaseLayer(l.id, layerId) && typeof l.source !== "string" ? { ...l, source: { ...l.source, data: geojson } } : l
          )
        );
      })
      .catch((err) => console.warn(`[useCmTableMapLayers] Active table fetch failed:`, err))
      .finally(() => {
        if (!cancelled) setIsCmTableLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTableSlug, tableSlugKey, cmTableLayerVersion, setIsCmTableLoading]);

  // 3. Background tables bulk fetch (Track B) with containment optimization
  useEffect(() => {
    if (!bbox || showOnlyFilter || activeTableLoadedRef.current === null) return;

    // Only include background tables that are currently toggled ON in the layer panel
    const backgroundEndpoints = spatialEndpoints.filter((ep) => {
      const layerId = `cm-table-${ep.tableSlug || ep.tableName}`;
      return (ep.tableSlug || ep.tableName) !== activeTableSlug && visibleLayers.has(layerId);
    });
    if (backgroundEndpoints.length === 0) return;

    const backgroundSlugs = backgroundEndpoints.map((ep) => ep.tableSlug || ep.tableName).sort();
    const slugsStr = backgroundSlugs.join(",");

    // Parse current bbox string to numbers for comparison
    const currentBbox = bbox.split(",").map(Number);
    if (currentBbox.length !== 4) return;

    // Optimization: Check if current view is already fully covered by the last fetch
    if (lastBulkFetchRef.current && lastBulkFetchRef.current.slugs === slugsStr) {
      if (isBboxContained(lastBulkFetchRef.current.bbox, currentBbox)) {
        return;
      }
    }

    let cancelled = false;

    setIsCmTableLoading(true);
    getCmTableBulkGeo(backgroundSlugs, bbox)
      .then((res) => {
        if (cancelled) return;

        // Update the "last fetched" tracking
        lastBulkFetchRef.current = { slugs: slugsStr, bbox: currentBbox };

        const perTable: Record<string, any[]> = {};
        backgroundSlugs.forEach((slug) => (perTable[slug] = []));

        res.data.features.forEach((f: any) => {
          // Use table_slug for grouping
          const slug = f.properties?.table_slug;
          if (slug && perTable[slug]) perTable[slug].push(f);
        });

        setLayers((prev) =>
          prev.map((l: any) => {
            const endpoint = backgroundEndpoints.find((ep) => `cm-table-${ep.tableSlug || ep.tableName}` === stripCmSubLayerSuffix(l.id));
            if (!endpoint || typeof l.source === "string") return l;
            return {
              ...l,
              source: { ...l.source, data: { type: "FeatureCollection", features: perTable[endpoint.tableSlug || endpoint.tableName] } }
            };
          })
        );
      })
      .catch((err) => console.warn("[useCmTableMapLayers] Bulk fetch failed:", err))
      .finally(() => {
        if (!cancelled) setIsCmTableLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [bbox, showOnlyFilter, activeTableSlug, tableSlugKey, visibleLayers, setIsCmTableLoading]);

  return { layers, selectedTableExtent };
};
